import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { ArrowLeft, Building2, Mail, UserCheck } from 'lucide-react'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command'
import { useAuth } from '~/hooks/useAuth'
import {
  IImpersonationIdentity,
  searchIdentitiesForImpersonation,
} from '~/modules/auth/data/authApi'

interface ImpersonateUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ImpersonateUserDialog({ open, onOpenChange }: Readonly<ImpersonateUserDialogProps>) {
  const { handleImpersonate, userId } = useAuth()
  const [identities, setIdentities] = useState<IImpersonationIdentity[]>([])
  const [selectedIdentity, setSelectedIdentity] = useState<IImpersonationIdentity | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)

  const handleSearch = useCallback(
    (value: string) => {
      if (searchTimeout) clearTimeout(searchTimeout)

      if (value.length < 2) {
        setIdentities([])
        return
      }

      const timeout = setTimeout(async () => {
        setLoading(true)
        try {
          const results = await searchIdentitiesForImpersonation(value)
          setIdentities(results)
        } catch {
          setIdentities([])
        } finally {
          setLoading(false)
        }
      }, 300)

      setSearchTimeout(timeout)
    },
    [searchTimeout],
  )

  const handleSelectIdentity = useCallback((identity: IImpersonationIdentity) => {
    setSelectedIdentity(identity)
  }, [])

  const handleBack = useCallback(() => {
    setSelectedIdentity(null)
  }, [])

  const handleSelectWorkspace = useCallback(
    async (targetUserId: string) => {
      setSubmitting(true)
      try {
        await handleImpersonate(targetUserId)
        onOpenChange(false)
        setSelectedIdentity(null)
        setIdentities([])
        toast.success('Impersonation started successfully.')
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to start impersonation.'
        toast.error(message)
      } finally {
        setSubmitting(false)
      }
    },
    [handleImpersonate, onOpenChange],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setSelectedIdentity(null)
        setIdentities([])
      }
      onOpenChange(open)
    },
    [onOpenChange],
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md'>
        {selectedIdentity ? (
          <>
            <DialogHeader>
              <div className='flex items-center gap-2'>
                <Button variant='ghost' size='icon' className='h-7 w-7' onClick={handleBack}>
                  <ArrowLeft className='h-4 w-4' />
                </Button>
                <div>
                  <DialogTitle>{selectedIdentity.name}</DialogTitle>
                  <DialogDescription className='flex items-center gap-1'>
                    <Mail className='h-3 w-3' />
                    {selectedIdentity.email}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div className='text-sm text-muted-foreground mb-1'>
              Select a workspace to impersonate:
            </div>
            <div className='max-h-[300px] overflow-y-auto space-y-1.5'>
              {selectedIdentity.workspaces
                .filter((w) => w.id !== userId && w.active)
                .map((workspace) => (
                  <button
                    key={workspace.id}
                    onClick={() => handleSelectWorkspace(workspace.id)}
                    disabled={submitting}
                    className='flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left text-sm bg-white transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none'
                  >
                    <Building2 className='h-4 w-4 text-muted-foreground shrink-0' />
                    <div className='flex flex-col flex-1 min-w-0'>
                      <span className='truncate font-medium'>
                        {workspace.workspace_name ?? workspace.company_name}
                      </span>
                      <span className='truncate text-xs text-muted-foreground capitalize'>
                        {workspace.profile}
                      </span>
                    </div>
                  </button>
                ))}
              {selectedIdentity.workspaces.filter((w) => w.id !== userId && w.active).length ===
                0 && (
                <div className='py-4 text-center text-sm text-muted-foreground'>
                  No available workspaces to impersonate.
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Impersonate User</DialogTitle>
              <DialogDescription>Search for a person by name or email.</DialogDescription>
            </DialogHeader>
            <Command shouldFilter={false}>
              <CommandInput
                placeholder='Search by name or email...'
                onValueChange={handleSearch}
                disabled={submitting}
              />
              <CommandList>
                {loading && (
                  <div className='py-6 text-center text-sm text-muted-foreground'>
                    Searching...
                  </div>
                )}
                {!loading && (
                  <CommandEmpty>No results found. Type at least 2 characters.</CommandEmpty>
                )}
                <CommandGroup>
                  {identities.map((identity) => (
                    <CommandItem
                      key={identity.identity_id}
                      value={identity.identity_id}
                      onSelect={() => handleSelectIdentity(identity)}
                      className='cursor-pointer'
                    >
                      <UserCheck className='mr-2 h-4 w-4 text-muted-foreground' />
                      <div className='flex flex-col flex-1 min-w-0'>
                        <span className='truncate font-medium'>{identity.name}</span>
                        <span className='truncate text-xs text-muted-foreground'>
                          {identity.email}
                        </span>
                      </div>
                      <span className='ml-2 text-xs text-muted-foreground shrink-0'>
                        {identity.workspaces.length} workspace
                        {identity.workspaces.length === 1 ? '' : 's'}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
